import { RolPlataforma } from '../models/novedades.models';
import { EstadoPatrocinio, EstadoPostulacion, Postulacion } from '../models/postulaciones.models';
import { normalizeSearchText } from './text-format.util';

type PostulacionConProyecto = Postulacion & {
  idProyecto?: string;
  contrato?: { idProyecto?: string };
};

export interface PostulacionesFiltroCriteria {
  rolActual: RolPlataforma;
  concursoSeleccionado?: string;
  subdireccionSeleccionada: string;
  estadoSeleccionado: string;
  anioSeleccionado: string;
  idPostulacionBusqueda: string;
  idProyectoBusqueda: string;
  folioBusqueda: string;
  proyectoBusqueda: string;
  subdireccionBusqueda: string;
  estadoBusqueda: string;
  departamentoBusqueda: string;
  busquedaGeneral: string;
  ordenarPor: 'fecha_inicio' | 'fecha_termino';
  ordenSeleccionado: 'asc' | 'desc';
}

export const ESTADOS_FILTRO_MIS_POSTULACIONES = ['En preparación', 'En revisión', 'Adjudicado'] as const;

export function etiquetaEstadoPostulacion(estado: EstadoPostulacion): string {
  if (estado === 'en_preparacion') return 'En preparación';
  if (estado === 'observada') return 'Observada';
  if (estado === 'enviada') return 'Enviada';
  if (estado === 'en_revision') return 'En revisión';
  if (estado === 'adjudicado') return 'Adjudicado';
  return 'Próximo';
}

export function esEstadoVisibleMisPostulaciones(estado: EstadoPostulacion): boolean {
  return estado === 'en_preparacion' || estado === 'en_revision' || estado === 'adjudicado';
}

export function esPostulacionEnviada(estado: EstadoPostulacion): boolean {
  return estado === 'en_revision' || estado === 'adjudicado';
}

export function avanceVisiblePostulacion(item: Postulacion): number {
  if (esPostulacionEnviada(item.estadoPostulacion)) return 100;
  const avance = item.avancePorcentaje ?? 0;
  return Math.min(99, Math.max(0, avance));
}

export function folioVisiblePostulacion(item: Postulacion): string {
  if (!esPostulacionEnviada(item.estadoPostulacion)) return '—';
  if (item.folio && item.folio !== '-') return item.folio;
  return '—';
}

export function etiquetaPatrocinio(estado: EstadoPatrocinio): string {
  if (estado === 'patrocinado') return 'Patrocinado';
  if (estado === 'sin_patrocinio') return 'Sin patrocinio';
  return '';
}

export function obtenerIdProyectoPostulacion(item: Postulacion): string {
  const itemConProyecto = item as PostulacionConProyecto;
  return itemConProyecto.contrato?.idProyecto ?? itemConProyecto.idProyecto ?? item.idPostulacion;
}

export function obtenerIdentificadorPostulacionPorRol(item: Postulacion, rol: RolPlataforma): string {
  if (rol === 'representante_institucional' || rol === 'ayudante_representante') {
    return obtenerIdProyectoPostulacion(item);
  }

  return item.idPostulacion;
}

export function filtrarYOrdenarPostulaciones(data: Postulacion[], criteria: PostulacionesFiltroCriteria): Postulacion[] {
  const baseRol = filtrarPorRol(data, criteria.rolActual);
  const filtradasPorSelectores = filtrarPorSelectores(baseRol, criteria);
  const filtradasPorTexto = filtrarPorTexto(filtradasPorSelectores, criteria);
  return ordenarPorFecha(filtradasPorTexto, criteria.ordenarPor, criteria.ordenSeleccionado);
}

function filtrarPorRol(data: Postulacion[], rol: RolPlataforma): Postulacion[] {
  return data.filter((item) => {
    if (!item.rolesPermitidos.includes(rol)) return false;
    if (rol === 'ayudante_representante') return item.solicitudPatrocinio;
    return true;
  });
}

function filtrarPorSelectores(data: Postulacion[], criteria: PostulacionesFiltroCriteria): Postulacion[] {
  let base = [...data];

  if (criteria.concursoSeleccionado && criteria.concursoSeleccionado !== 'Todos') {
    base = base.filter((p) => p.concurso === criteria.concursoSeleccionado);
  }

  if (criteria.subdireccionSeleccionada !== 'Todas') {
    base = base.filter((p) => p.subdireccion === criteria.subdireccionSeleccionada);
  }

  if (criteria.estadoSeleccionado !== 'Todos') {
    base = base.filter((p) => etiquetaEstadoPostulacion(p.estadoPostulacion) === criteria.estadoSeleccionado);
  }

  if (criteria.anioSeleccionado !== 'Todos') {
    const anio = Number(criteria.anioSeleccionado);
    base = base.filter((p) => p.anio === anio);
  }

  return base;
}

function filtrarPorTexto(data: Postulacion[], criteria: PostulacionesFiltroCriteria): Postulacion[] {
  const idPostulacion = normalizar(criteria.idPostulacionBusqueda);
  const idProyecto = normalizar(criteria.idProyectoBusqueda);
  const folio = normalizar(criteria.folioBusqueda);
  const proyecto = normalizar(criteria.proyectoBusqueda);
  const subdireccionTxt = normalizar(criteria.subdireccionBusqueda);
  const estadoTxt = normalizar(criteria.estadoBusqueda);
  const departamentoTxt = normalizar(criteria.departamentoBusqueda);
  const busquedaGeneral = normalizar(criteria.busquedaGeneral);

  return data.filter((p) => {
    const coincideIdPostulacion = idPostulacion ? normalizar(p.idPostulacion).includes(idPostulacion) : true;
    const coincideIdProyecto = idProyecto ? normalizar(obtenerIdProyectoPostulacion(p)).includes(idProyecto) : true;
    const coincideFolio = folio ? normalizar(folioVisiblePostulacion(p)).includes(folio) : true;
    const coincideProyecto = proyecto
      ? normalizar(p.concurso).includes(proyecto) || normalizar(p.nombreProyecto).includes(proyecto)
      : true;
    const coincideSubdireccion = subdireccionTxt ? normalizar(p.subdireccion).includes(subdireccionTxt) : true;
    const estadoEtiqueta = normalizar(etiquetaEstadoPostulacion(p.estadoPostulacion));
    const coincideEstado = estadoTxt ? estadoEtiqueta.includes(estadoTxt) : true;
    const coincideDepartamento = departamentoTxt ? normalizar(p.departamentoFacultad).includes(departamentoTxt) : true;
    const coincideBusquedaGeneral = busquedaGeneral
      ? normalizar(p.nombreProyecto).includes(busquedaGeneral) || normalizar(p.idPostulacion).includes(busquedaGeneral)
      : true;

    return (
      coincideIdPostulacion &&
      coincideIdProyecto &&
      coincideFolio &&
      coincideProyecto &&
      coincideSubdireccion &&
      coincideEstado &&
      coincideDepartamento &&
      coincideBusquedaGeneral
    );
  });
}

function ordenarPorFecha(
  data: Postulacion[],
  campo: 'fecha_inicio' | 'fecha_termino',
  orden: 'asc' | 'desc'
): Postulacion[] {
  return [...data].sort((a, b) => {
    const fechaA = new Date(campo === 'fecha_inicio' ? a.fechaInicio : a.fechaTermino).getTime();
    const fechaB = new Date(campo === 'fecha_inicio' ? b.fechaInicio : b.fechaTermino).getTime();
    return orden === 'asc' ? fechaA - fechaB : fechaB - fechaA;
  });
}

function normalizar(valor: string): string {
  return normalizeSearchText(valor);
}
